/**
 * Spyglass (SN1): the screen and the camera in one picture — a demonstration, a
 * webinar, a walk through the browser — recorded as a take, or shared on a
 * live. The screen fills the frame; the camera sits in a bubble in a corner.
 * Everything here is pure geometry so the layout is tested without a browser;
 * `drawSpyglassFrame` does the drawing with whatever canvas it is given.
 */
export type Corner = 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
export interface Bubble { corner: Corner; size: number; shape: 'circle' | 'rounded'; hidden?: boolean }
export const DEFAULT_BUBBLE: Bubble = { corner: 'bottom-right', size: 22, shape: 'circle' };

export interface Rect { x: number; y: number; w: number; h: number }

/** Fit a picture of `sw`×`sh` inside `W`×`H` without cropping, centred (letterboxed). */
export function fitInside(sw: number, sh: number, W: number, H: number): Rect {
  if (sw <= 0 || sh <= 0) return { x: 0, y: 0, w: W, h: H };
  const k = Math.min(W / sw, H / sh);
  const w = Math.round(sw * k), h = Math.round(sh * k);
  return { x: Math.round((W - w) / 2), y: Math.round((H - h) / 2), w, h };
}

/** Cover a box with a picture of `sw`×`sh`: the source rect to crop so it fills without stretching. */
export function coverCrop(sw: number, sh: number, w: number, h: number): Rect {
  const k = Math.max(w / sw, h / sh);
  const cw = Math.round(w / k), ch = Math.round(h / k);
  return { x: Math.round((sw - cw) / 2), y: Math.round((sh - ch) / 2), w: cw, h: ch };
}

/** The camera bubble: a square of `size`% of the frame's height, in a corner, off the edge by a margin. */
export function bubbleRect(b: Bubble, W: number, H: number): Rect {
  const size = Math.round((Math.max(10, Math.min(45, b.size)) / 100) * H);
  const m = Math.round(H * 0.03);
  const x = b.corner.endsWith('right') ? W - m - size : m;
  const y = b.corner.startsWith('bottom') ? H - m - size : m;
  return { x, y, w: size, h: size };
}

/** The recording's frame: the screen's own size, capped at 1920 wide and even on both sides (encoders want that). */
export function frameSize(sw: number, sh: number): { W: number; H: number } {
  if (sw <= 0 || sh <= 0) return { W: 1920, H: 1080 };
  const k = Math.min(1, 1920 / sw);
  const even = (n: number) => Math.max(2, Math.round(n / 2) * 2);
  return { W: even(sw * k), H: even(sh * k) };
}

type Drawable = { videoWidth: number; videoHeight: number; readyState: number };
type Ctx = {
  fillStyle: string | CanvasGradient | CanvasPattern; strokeStyle: string | CanvasGradient | CanvasPattern; lineWidth: number;
  fillRect(x: number, y: number, w: number, h: number): void;
  drawImage(img: CanvasImageSource, sx: number, sy: number, sw: number, sh: number, dx: number, dy: number, dw: number, dh: number): void;
  save(): void; restore(): void; beginPath(): void; closePath(): void; clip(): void; stroke(): void;
  arc(x: number, y: number, r: number, a: number, b: number): void;
  roundRect(x: number, y: number, w: number, h: number, r: number): void;
  translate(x: number, y: number): void; scale(x: number, y: number): void;
};

/** One frame: the screen letterboxed on a dark ground, the camera in its bubble with a ring in the show's colour. */
/** SN4: zoom — the part of the screen (in fractions of it) that fills the frame at factor `k`, centred on a spot, never past the edges. */
export interface Zoom { x: number; y: number; k: number }
export function zoomCrop(z: Zoom | null | undefined, sw: number, sh: number): Rect {
  if (!z || z.k <= 1.001) return { x: 0, y: 0, w: sw, h: sh };
  const w = sw / z.k, h = sh / z.k;
  const x = Math.max(0, Math.min(sw - w, z.x * sw - w / 2));
  const y = Math.max(0, Math.min(sh - h, z.y * sh - h / 2));
  return { x: Math.round(x), y: Math.round(y), w: Math.round(w), h: Math.round(h) };
}

/** One step of an eased zoom: the current zoom moves a fifth of the way to the target each frame. */
export function easeZoom(cur: Zoom, target: Zoom): Zoom {
  const k = cur.k + (target.k - cur.k) * 0.2;
  return { x: cur.x + (target.x - cur.x) * 0.2, y: cur.y + (target.y - cur.y) * 0.2, k: Math.abs(k - target.k) < 0.005 ? target.k : k };
}

export function drawSpyglassFrame(ctx: Ctx, W: number, H: number, screen: Drawable | null, cam: Drawable | null, b: Bubble, accent = '#e0b100', mirror = true, zoom: Zoom | null = null): void {
  ctx.fillStyle = '#101418';
  ctx.fillRect(0, 0, W, H);
  if (screen && screen.readyState >= 2 && screen.videoWidth > 0) {
    const c = zoomCrop(zoom, screen.videoWidth, screen.videoHeight);
    const f = fitInside(c.w, c.h, W, H);
    ctx.drawImage(screen as unknown as CanvasImageSource, c.x, c.y, c.w, c.h, f.x, f.y, f.w, f.h);
  }
  if (!cam || b.hidden || cam.readyState < 2 || cam.videoWidth === 0) return;
  const r = bubbleRect(b, W, H);
  const crop = coverCrop(cam.videoWidth, cam.videoHeight, r.w, r.h);
  ctx.save();
  ctx.beginPath();
  if (b.shape === 'circle') ctx.arc(r.x + r.w / 2, r.y + r.h / 2, r.w / 2, 0, Math.PI * 2);
  else ctx.roundRect(r.x, r.y, r.w, r.h, Math.round(r.w * 0.12));
  ctx.closePath();
  ctx.clip();
  if (mirror) { ctx.translate(r.x + r.w, r.y); ctx.scale(-1, 1); ctx.drawImage(cam as unknown as CanvasImageSource, crop.x, crop.y, crop.w, crop.h, 0, 0, r.w, r.h); }
  else ctx.drawImage(cam as unknown as CanvasImageSource, crop.x, crop.y, crop.w, crop.h, r.x, r.y, r.w, r.h);
  ctx.restore();
  ctx.beginPath();
  if (b.shape === 'circle') ctx.arc(r.x + r.w / 2, r.y + r.h / 2, r.w / 2, 0, Math.PI * 2);
  else ctx.roundRect(r.x, r.y, r.w, r.h, Math.round(r.w * 0.12));
  ctx.lineWidth = Math.max(3, Math.round(H * 0.006));
  ctx.strokeStyle = accent;
  ctx.stroke();
}

/**
 * One stream for the recorder: the composed picture from the canvas, and the
 * sound — the microphone and, when the screen came with sound (a tab), that
 * too, mixed. Returns the stream and a stop for the mixer.
 */
export function mixForRecorder(canvas: HTMLCanvasElement, mic: MediaStream | null, screen: MediaStream | null, fps = 30): { stream: MediaStream; close: () => void } {
  const out = canvas.captureStream(fps);
  const audioTracks = [...(mic?.getAudioTracks() ?? []), ...(screen?.getAudioTracks() ?? [])];
  if (audioTracks.length === 0) return { stream: out, close: () => undefined };
  if (audioTracks.length === 1 || typeof AudioContext === 'undefined') { out.addTrack(audioTracks[0]!); return { stream: out, close: () => undefined }; }
  const ac = new AudioContext();
  const dest = ac.createMediaStreamDestination();
  for (const t of audioTracks) ac.createMediaStreamSource(new MediaStream([t])).connect(dest);
  dest.stream.getAudioTracks().forEach((t) => out.addTrack(t));
  return { stream: out, close: () => { void ac.close(); } };
}
